/**
 * Fixture state for the three system-measurement doors:
 * `GET /ops/v1/system/components`, `GET /ops/v1/system/queues` (O4) and
 * `GET /ops/v1/system/workers` (DA-09, wired to a screen by TLM-4b).
 *
 * This module is a COPY of the server's closed vocabularies and of the server's
 * own note, and it says so in every name — the same deal `fixtures/matrix.ts`
 * struck for the permission matrix:
 *
 *  - `OUTBOX_STATUSES` mirrors `acs_service/store.py::OUTBOX_STATUSES` — the four
 *    states an outbox row can sit in (pending / claimed / sent / failed), in the
 *    server's order. The server builds the tuple out of four named constants
 *    (`OUTBOX_PENDING` …), and the guard resolves those names, so a fifth
 *    spelling in either place is a red test and not a silent disagreement.
 *  - `JOB_STATUSES` mirrors `acs_service/job_machine.py::LIFECYCLE` — the seven
 *    lifecycle words, in the server's order.
 *  - `COUNT_KEYS` mirrors `acs_service/store.py::COUNT_KEYS` — the domain
 *    vocabulary the store's `counts()` answers in, NOT the raw collection names
 *    (F13: which backend answers must never change the shape of the payload).
 *  - `QUEUES_NOTE` mirrors `acs_service/routers/system.py::QUEUES_NOTE` VERBATIM.
 *  - `WORKER_NAMES` mirrors `acs_service/worker_health.py::WORKER_NAMES` — the loops the
 *    deployment is expected to run, which the payload cannot say for itself, because a
 *    worker that never beat is ABSENT rather than reported.
 *  - `WORKERS_NOTE` mirrors `acs_service/routers/system.py::WORKERS_NOTE` VERBATIM.
 *
 * The note is the important one. It is the honesty clause of the surface: it
 * names the four metric families the façade deliberately does NOT publish and
 * the counting defect that keeps each of them absent. A fixture that
 * PARAPHRASED it would teach the console a sentence the server never says —
 * and the whole point of the note is that it is the server's own sentence. So
 * the fixture holds the same string, and `test/systemShapes.test.ts`
 * reads the Python source and fails when the two drift.
 *
 * THE MISSING-DEPTH RULE, which this module exists to keep honest: a depth the
 * server did not send is NOT a zero. The server sends the WHOLE closed
 * vocabulary when it measured (a word nobody holds is a MEASURED zero, because
 * the store counted it and found none), and `depths: null` when it could not
 * measure at all. Both cases are representable here, and neither is ever
 * flattened into "0".
 *
 * THE MISSING-WORKER RULE is the same rule at the other end of the surface: a worker
 * with no heartbeat row is ABSENT, and absent is neither `down` nor `ok`. The last
 * block of this module keeps that representable too — four rows and two deliberate
 * absences, so a page has to learn to say «this façade has not heard from it».
 */
import type { SystemWorkerBeat } from '../api/types';

/**
 * The fixture's measurement of the outbox, in the server's four states.
 *
 * The OBJECT IS THE VOCABULARY: the closed alphabet is this object's own key
 * set, so the two cannot drift apart — a fixture that lost a word would have
 * to lose it from the measurement itself, and the guard test below compares
 * this key set with the server's MEASURED one. A word nobody holds is a
 * MEASURED zero (the store counted it and found none), never an absence.
 */
export const FIXTURE_OUTBOX_DEPTHS: Readonly<Record<string, number>> = {
  pending: 4,
  claimed: 1,
  sent: 27,
  failed: 2,
};

/** The four outbox states, in the fixture's order (`store.py::OUTBOX_STATUSES`). */
export const OUTBOX_STATUSES: readonly string[] = Object.keys(FIXTURE_OUTBOX_DEPTHS);

/**
 * The fixture's measurement of the jobs, in the server's seven lifecycle words.
 *
 * `timeout` and `cancelled` are MEASURED ZEROS: the fixture counted them and
 * found none. They are here because the vocabulary is closed — a word that
 * were missing would be a vocabulary the server does not have.
 */
export const FIXTURE_JOB_DEPTHS: Readonly<Record<string, number>> = {
  queued: 3,
  await_inform: 1,
  running: 2,
  completed: 18,
  failed: 1,
  timeout: 0,
  cancelled: 0,
};

/** The seven lifecycle words, in the fixture's order (`job_machine.py::LIFECYCLE`). */
export const JOB_STATUSES: readonly string[] = Object.keys(FIXTURE_JOB_DEPTHS);

/**
 * The store's `counts()` in the fixture deployment.
 *
 * The KEYS are the domain vocabulary of `store.py::COUNT_KEYS` — NOT the raw
 * collection names (F13: which backend answers must never change the shape of
 * the payload).
 */
export const FIXTURE_COUNTS: Readonly<Record<string, number>> = {
  devices: 12,
  bindings: 11,
  informs: 148,
  jobs: 25,
  outbox: 34,
  audit: 96,
  users: 3,
  sessions: 2,
};

/** The domain vocabulary of the store's `counts()` (`store.py::COUNT_KEYS`). */
export const COUNT_KEYS: readonly string[] = Object.keys(FIXTURE_COUNTS);

/**
 * The honesty clause of the queues door — a VERBATIM copy of the server's
 * `routers/system.py::QUEUES_NOTE`, fragment for fragment.
 *
 * It is split across the lines the Python splits it across, so a reader can
 * compare the two side by side, and the guard test compares the CONCATENATED
 * value (which is what the server actually sends) with the concatenated value
 * here.
 */
export const QUEUES_NOTE =
  'Queue depths are COUNTED, not estimated: one store read ' +
  '(Store.queue_depths — no row is fetched) and a live filter over the ' +
  'session table. ' +
  'Deliberately NOT published, because of a named counting defect: ' +
  'session rate (no windowed counter exists, and the collection size is ' +
  "not monotonic — purging removes rows). DA-09's second half dissolved " +
  'the other three former absences by MAKING them countable — inform and ' +
  'adapter-error counters bumped at the write site (Store.metric_bump), ' +
  'and job latency as a backlog-age gauge over the oldest non-terminal ' +
  "job. An absent metric is never a zero; a counter's zero is a fact.";

/**
 * The measured depths of the fixture deployment.
 *
 * The numbers are the fixture's own snapshot of its own queue — the same
 * relationship the server's has to its store: a depth the fixture measured.
 * The VOCABULARY is what matters and it is the server's, whole: every word of
 * both closed alphabets is here, and a word with no rows behind it is a
 * MEASURED zero (the fixture counted it and found none) — never an absence.
 */

/** The live console sessions of the fixture deployment. */
export const FIXTURE_SESSIONS_ACTIVE = 2;

/* ---- the workers door (DA-09, wired to a screen by TLM-4b) ----
   The two sisters above are counts; this one is a JUDGEMENT, and the judgement is the
   server's. The page that renders it must not compute a state of its own (`age_seconds`
   compared, an interval multiplied), so what the fixture sends is a row already labelled
   `ok` / `failing` / `stale` exactly as `judge_workers()` would have labelled it — and
   `stale_after_seconds` is written down per row rather than derived here, because
   `3 × interval + 60` is the server's rule and a second copy of a rule is a rule that can
   disagree with itself. */

/**
 * The worker classes this deployment is expected to run — a VERBATIM copy of
 * `acs_service/worker_health.py::WORKER_NAMES`, in the server's order.
 *
 * The door sends a row ONLY for a worker that has beaten, so the payload alone cannot tell a
 * caller which loops it was looking for: «no row» means either «this façade has not heard from
 * it» or «nobody runs it here», and those are different sentences. The expected set therefore
 * has to be mirrored, and — as with `QUEUES_NOTE` — the mirror is checked against the Python
 * source, not against memory: a seventh loop added server-side reddens `systemShapes.test.ts`
 * until this list follows, and a name invented here reddens it the other way.
 */
export const WORKER_NAMES = [
  'acs-sync',
  'acs-verify',
  'acs-outbox',
  'acs-retention',
  'acs-campaigns',
  'acs-optical',
  'acs-drift',
  'acs-notify',
] as const;

/**
 * The honesty clause of the workers door — a VERBATIM copy of
 * `routers/system.py::WORKERS_NOTE`, fragment for fragment, for the same reason the
 * queues note is: the note IS the surface's claim about itself, and a paraphrase would
 * teach the console a sentence the door never says.
 */
export const WORKERS_NOTE =
  'Worker heartbeats are WRITTEN BY THE WORKERS, not probed: one row per ' +
  'worker class, refreshed after every pass with the cadence that worker ' +
  'actually runs on, and judged stale when the row is older than three ' +
  'cadences plus sixty seconds — a daily retention pass is judged on a daily ' +
  'clock, the outbox on its five-second one. A worker this façade has never ' +
  'received a beat from is ABSENT from this answer, not down: the API cannot ' +
  'know whether this deployment runs it, and an invented down is as wrong as ' +
  'an invented ok. `owner` names the replica that beat last; which replica ' +
  'died is not published. Inform rate, adapter errors and job backlog age ' +
  "are published on /metrics since DA-09's second half (persistent " +
  'counters and a backlog-age gauge); session rate stays absent for the ' +
  'counting defect named there.';

/**
 * The fixture deployment's heartbeat table — four rows, two absences, all three words.
 *
 * The shapes this table exists to make reachable:
 *  - `acs-outbox` is **failing**: alive, beating every five seconds, last pass in the
 *    exception path. A failing worker is not a dead one, and the next tick is the retry.
 *  - `acs-verify` is **stale** and its last pass failed too: `stale` outranks `failing`,
 *    because a worker that has not beaten inside its bound is dead whatever that last
 *    pass said. This row is the precedence, on the screen.
 *  - `acs-retention` is **ok** at 43 000 seconds. On the outbox's clock that same age
 *    would be a corpse — this is the whole reason the bound is the worker's own cadence.
 *  - `acs-campaigns`, `acs-optical` and `acs-drift` are **absent**. Not `down`, not `ok`: the
 *    fixture is a façade that has never received a beat from them, which is a different sentence
 *    from «it is broken», and collapsing the two is the invention DA-09 refused to make.
 */
export const FIXTURE_WORKER_BEATS: readonly SystemWorkerBeat[] = [
  {
    worker: 'acs-sync',
    status: 'ok',
    owner: 'pod-1',
    at: '',
    age_seconds: 40,
    interval_seconds: 300,
    stale_after_seconds: 960,
    beats: 118,
    failures: 0,
    detail: { devices: 12 },
  },
  {
    worker: 'acs-verify',
    status: 'stale',
    owner: 'pod-1',
    at: '',
    age_seconds: 1400,
    interval_seconds: 300,
    stale_after_seconds: 960,
    beats: 96,
    failures: 1,
    detail: { jobs: 3 },
  },
  {
    worker: 'acs-outbox',
    status: 'failing',
    owner: 'pod-2',
    at: '',
    age_seconds: 6,
    interval_seconds: 5,
    stale_after_seconds: 75,
    beats: 4210,
    failures: 2,
    detail: { events: 27 },
  },
  {
    worker: 'acs-retention',
    status: 'ok',
    owner: 'pod-1',
    at: '',
    age_seconds: 43000,
    interval_seconds: 86400,
    stale_after_seconds: 259260,
    beats: 7,
    failures: 0,
    detail: { families: 8 },
  },
];

/**
 * The same table with its stamps filled in, for the moment it is answered.
 *
 * `at` is the instant the row claims, and it has to be coherent with `age_seconds` and with
 * the payload's own `time` — a fixture that printed a fixed date beside «40 seconds ago»
 * would be a fixture an operator could not read. The ages stay the server's answer: this
 * only writes the instant they came from.
 */
export function fixtureWorkerBeats(nowMs: number): Record<string, SystemWorkerBeat> {
  const rows: Record<string, SystemWorkerBeat> = {};
  for (const beat of FIXTURE_WORKER_BEATS) {
    const age = beat.age_seconds;
    rows[beat.worker] = {
      ...beat,
      at: new Date(nowMs - (age ?? 0) * 1000).toISOString(),
      detail: { ...beat.detail },
    };
  }
  return rows;
}
